import React from 'react';
import Icon from '../Common/Icon';

/**
 * PrivacyContent Component
 *
 * Simplified Privacy Policy content for modal display
 */
const PrivacyContent = () => {
  return (
    <div className="policy-content">
      <p style={{ marginBottom: '20px', fontSize: '15px', fontWeight: 600 }}>
        Your data belongs to you. Always.
      </p>

      <h2>Four Tiers to Choose From</h2>

      <h3><Icon name="globe-2" size={18} strokeWidth={2} /> Explorer (Free)</h3>
      <p>
        Perfect for exploring. 30-day observation history, 3 active experiments. Free forever.
      </p>

      <h3><Icon name="star" size={18} strokeWidth={2} /> Believer ($9/month)</h3>
      <p>
        Unlimited observations and experiments. AI is optional; connect via your own API keys from cloud providers.
      </p>

      <h3><Icon name="shield" size={18} strokeWidth={2} /> Guardian ($25/month)</h3>
      <p>
        Everything in Believer + 500 AI calls/day, advanced analytics, priority support.
      </p>

      <h3><Icon name="infinity" size={18} strokeWidth={2} /> Lifetime ($299 one-time)</h3>
      <p>
        Own it forever. All features unlimited. Optional local LLM support for 100% offline AI.
      </p>

      <h2>Our Privacy Principles</h2>

      <h3><Icon name="lock" size={18} strokeWidth={2} /> Zero-Knowledge Sync</h3>
      <p>
        <strong>Web app:</strong> Your observations are encrypted on your device with libsodium before syncing to our self-hosted PocketBase in Germany (EU). Only ciphertext leaves your browser, and encryption keys never do.
      </p>
      <p>
        <strong>Desktop app:</strong> Offline-first. Your records are saved locally first, in an encrypted local database. Cloud sync is optional/manual, and Lifetime users can cherry-pick dataset scope before syncing (Settings → Cloud Sync &amp; Encryption → Sync now).
        Sync Integrity Center provides reliability score visibility plus repair actions, stale-failure auto-heal, failed-queue recovery controls, Trust Layer Timeline, and diagnostics export.
      </p>
      <p>
        We operate a <strong>zero-knowledge architecture</strong>: we cannot read, mine, or sell your personal data.
      </p>

      <h3><Icon name="bot" size={18} strokeWidth={2} /> AI Co-Pilot is Optional</h3>
      <p>
        <strong>AI is completely optional on all plans.</strong> You can use ThryveLoop without AI.
      </p>
      <p>
        <strong>Believer & Guardian Plans:</strong> If you enable AI, use your own API keys to connect to external cloud providers (OpenAI, Claude, DeepSeek AI, Kimi, OpenRouter, etc.).
        In the hosted web app, requests are relayed through an authenticated ThryveLoop proxy to your configured provider endpoint over encrypted connections.
      </p>
      <p>
        <strong>Lifetime Plan:</strong> If you enable AI, optional desktop app helps you download and connect local LLMs that run <strong>100% locally</strong> on your device.
        Your thoughts never leave your computer.
      </p>
      <p>
        <strong>You are always in the loop:</strong> AI suggests, you decide.
      </p>

      <h3><Icon name="cloud" size={18} strokeWidth={2} /> Private Sync, On Your Terms</h3>
      <p>
        Explorer keeps a 30-day history and supports up to 3 active experiments. Believer, Guardian, and Lifetime sync ciphertext to PocketBase using end-to-end encryption so you can log in from any browser while keeping full control. You can export JSON/CSV anytime, clear synced data, or delete your account.
      </p>

      <h3><Icon name="eye" size={18} strokeWidth={2} /> No Tracking or Analytics</h3>
      <p>
        We have no idea what experiments you're running or what energizes you. Your journey is yours alone.
      </p>

      <h2>What We Actually Collect</h2>

      <p><strong>Web App:</strong> Encrypted observations, experiments, journals, and settings stored in PocketBase (ciphertext only) so you can sign in from multiple devices.</p>
      <p><strong>Desktop App:</strong> Same encrypted dataset stored locally first (encrypted at rest). Optional cloud sync stores ciphertext only.</p>
      <p><strong>Lemon Squeezy (Payment):</strong> Email only for delivery & license management.</p>
      <p><strong>PocketBase (Authentication & Sync):</strong> Email, license tier, and encrypted data blobs (plus minimal metadata required for sync). We can&rsquo;t decrypt your content.</p>

      <h2>You Are In Complete Control</h2>

      <p>
        <strong><Icon name="upload" size={16} strokeWidth={2.2} /> Export Your Data:</strong> Download all observations and experiments as JSON or CSV anytime.
      </p>
      <p>
        <strong><Icon name="trash-2" size={16} strokeWidth={2.2} /> Delete Your Data:</strong> Clear everything from your browser or desktop app. We can't recover it because we never had access.
      </p>
      <p>
        <strong><Icon name="octagon" size={16} strokeWidth={2.2} /> Delete Your Account:</strong> Permanently deletes your encrypted server data, profile, and license record.
      </p>
      <p>
        <strong><Icon name="settings" size={16} strokeWidth={2.2} /> Choose Your AI (Optional):</strong> Believer & Guardian users connect via API keys. Lifetime users can optionally connect local LLMs. AI is optional on all plans.
      </p>

      <h2>Important Note About AI & Privacy</h2>
      <p style={{ padding: '12px', background: 'rgba(251, 188, 5, 0.1)', borderLeft: '3px solid #f59e0b', marginTop: '16px' }}>
        <strong><Icon name="alert-triangle" size={16} strokeWidth={2.2} /> Cloud AI Privacy:</strong> If you enable AI on Believer or Guardian plans, you'll need API keys from third-party cloud providers (OpenAI, Claude, DeepSeek AI, Kimi, OpenRouter, etc.).
        In the hosted web app, requests are relayed through an authenticated ThryveLoop proxy to your configured provider endpoint, then processed by that provider according to its privacy policy.
        For maximum privacy with local AI processing, upgrade to Lifetime ($299 one-time) which includes optional local LLM support for 100% offline AI on your device.
      </p>

      <h2>Updates to This Policy</h2>
      <p>
        If we change how we handle privacy, we'll update this page. Your privacy will never be compromised.
      </p>
    </div>
  );
};

export default PrivacyContent;
